import { z } from 'zod';
import { exactMatchTime, validStringCharacterMessage, validStringCharacters } from './shared-validators/regex.ts';
import iterationSchema from './Iteration.schema.ts';
import {
  AWARENESS_CAMPAIGN_TARGETS,
  CAMPAIGN_TYPES,
  ITERATION_FREQUENCY,
  ITERATION_TYPE,
  VACCINATION_CAMPAIGN_TARGETS,
} from './enums/campaigns.ts';
import { validateDate } from '../utils/validatorUtils.ts';
import { CohortMetadata } from './metadata/cohorts.ts';
import UserInfoSchema from './UserInfo.schema.ts';

const restrictedString = z.string().regex(validStringCharacters, validStringCharacterMessage);

const invalidEmailMessage = 'Enter one or more email addresses in the correct format, like name@nhs.net. If you enter more than one email address, separate them with a comma. For example, \'person@nhs.net,person@nhs.net\'.';

export const CampaignTargetSchema = z.enum([...VACCINATION_CAMPAIGN_TARGETS, ...AWARENESS_CAMPAIGN_TARGETS]);
export type CampaignTarget = z.infer<typeof CampaignTargetSchema>;

export const campaignDetailsSchema = (cohortData: CohortMetadata[]) => {
  const schema = iterationSchema(cohortData);

  return z.object({
    ID: z.string().optional(),
    Version: z.number().optional(),
    Name: restrictedString.min(1, { message: 'Campaign Name is required' }).optional(),
    Type: z.enum(CAMPAIGN_TYPES).optional(),
    Target: CampaignTargetSchema.optional(),
    Manager: z.array(z.string().email(invalidEmailMessage)).min(1).optional(),
    Approver: z.array(z.string().email(invalidEmailMessage)).min(1).optional(),
    Reviewer: z.array(z.string().email(invalidEmailMessage)).min(1).optional(),
    IterationFrequency: z.enum(ITERATION_FREQUENCY).optional(),
    IterationType: z.enum(ITERATION_TYPE).optional(),
    IterationTime: z.string().regex(exactMatchTime).optional(),
    DefaultCommsRouting: z.string().optional(),
    StartDate: z.string().superRefine(validateDate).optional(),
    EndDate: z.string().superRefine(validateDate).optional(),
    ApprovalMinimum: z.number().optional(),
    ApprovalMaximum: z.number().optional(),
    Iterations: z.array(schema).optional(),
    AvailableRoutingIds: z.array(z.string()).optional(),
    Template: z.string().optional(), // guid:version
  });
};

export const iterationDetailsSchema = z.object({
  ID: z.string(),
  created_by: UserInfoSchema,
  creation_timestamp: z.number(),
  last_modified_by: UserInfoSchema,
  last_modified_timestamp: z.number(),
  locked_by: UserInfoSchema,
  status: z.string().optional(),
  submitted_by: z.string().optional(),
  reviewers: z.array(z.string()).optional(),
});

export type IterationDetails = z.infer<typeof iterationDetailsSchema>;

export const campaignConfigSchema = (cohortData: CohortMetadata[]) => z.object({
  CampaignConfig: campaignDetailsSchema(cohortData),
  /// the properties below have to be optional to be backwards compatible with old config files
  locked_by: UserInfoSchema.optional(),
  created_by: UserInfoSchema.optional(),
  creation_timestamp: z.number().optional(),
  last_modified_by: UserInfoSchema.optional(),
  last_modified_timestamp: z.number().optional(),
  iteration_details: z.array(iterationDetailsSchema).optional(),
});
export type CampaignConfig = z.infer<ReturnType<typeof campaignConfigSchema>>;

export const campaignDetailsFormSchema = campaignDetailsSchema([]).pick({
  Name: true,
  Type: true,
  Target: true,
  StartDate: true,
  EndDate: true,
}).required();

export const campaignManagementFormSchema = campaignDetailsSchema([]).pick({
  Manager: true,
  Approver: true,
  Reviewer: true,
}).required();

export type CampaignDetails = z.infer<typeof campaignDetailsFormSchema>;
export type CampaignManagementDetails = z.infer<typeof campaignManagementFormSchema>;

// Used to convert old config files with campaign emails stored as strings
// into new array format
export interface CampaignConfigLegacy {
  Manager: string | Array<string>;
  Approver: string | Array<string>;
  Reviewer: string | Array<string>;
}

export interface ConfigFileLegacy {
  CampaignConfig: CampaignConfigLegacy;
}

export default campaignConfigSchema;
